import { Box, Typography } from '@mui/material'
import CustomBarChart from 'common/components/CustomBarChart'
import CustomLineChart from 'common/components/CustomLineChart'
import CustomTable from 'common/components/CustomTable'
import Kpi from 'common/components/Kpi'

export default function KpisOptimizacion({
  kpis,
  produccionDiaria,
  produccionDiariaPorArticulo,
  fechaInicio,
}) {
  // Si no hay datos, mostrar mensaje
  if (!kpis && (!produccionDiaria || produccionDiaria.length === 0)) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          No hay KPIs disponibles para esta planificación
        </Typography>
      </Box>
    )
  }

  // Convierte el número de día (1-based) a una etiqueta de fecha "DD/MM"
  const diaToLabel = (dia) => {
    if (!fechaInicio) return `Día ${dia}`
    const fecha = new Date(fechaInicio.replace(/[Z]$/, '').replace(/[+-]\d{2}:\d{2}$/, ''))
    fecha.setDate(fecha.getDate() + (dia - 1))
    return fecha.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  // Formatear datos para el gráfico (convertir de kg a toneladas)
  const chartLabels = produccionDiaria?.map((item) => diaToLabel(item.dia)) || []
  const chartData =
    produccionDiaria?.map((item) => parseFloat((item.cantidad_total / 1000).toFixed(2))) || []

  // Preparar datos pivotados: una fila por pedido, columnas dinámicas por día
  const prepararDatosTabla = () => {
    if (!produccionDiariaPorArticulo || produccionDiariaPorArticulo.length === 0) {
      return { filas: [], columnas: [] }
    }

    // Obtener días únicos ordenados
    const diasSet = new Set()
    produccionDiariaPorArticulo.forEach((item) => diasSet.add(item.dia))
    const dias = Array.from(diasSet).sort((a, b) => a - b)

    // Agrupar por id_pedido o articulo (una fila por pedido)
    const pedidosMap = {}
    let autoId = 0
    const tieneDetallePedido = produccionDiariaPorArticulo.some((item) => item.id_pedido != null)

    produccionDiariaPorArticulo.forEach((item) => {
      const key = item.id_pedido ?? `auto_${item.articulo}_${autoId++}`
      if (!pedidosMap[key]) {
        pedidosMap[key] = {
          _rowId: key,
          numero_pedido: item.numero_pedido,
          numero_programacion: item.numero_programacion,
          articulo: item.articulo,
          maquina: item.maquina,
          fecha_limite: item.fecha_limite,
          cantidad_demandada: item.cantidad_demandada,
        }
      }
      pedidosMap[key][`dia_${item.dia}`] = item.cantidad
    })

    // Columnas fijas (solo mostrar detalle de pedido si la API lo incluye)
    const columnasFijas = tieneDetallePedido
      ? [
          { value: 'numero_pedido', key: 'Nº Pedido', width: 110, sortable: true },
          { value: 'numero_programacion', key: 'Nº Prog.', width: 80, sortable: true },
          { value: 'articulo', key: 'Artículo', flex: 1, sortable: true },
          { value: 'maquina', key: 'Máquina', width: 90, sortable: true },
          {
            value: 'fecha_limite',
            key: 'F. Límite',
            width: 110,
            sortable: true,
            type: 'number',
          },
          {
            value: 'cantidad_demandada',
            key: 'Cant. Demandada',
            width: 120,
            sortable: true,
            type: 'number',
          },
        ]
      : [{ value: 'articulo', key: 'Artículo', flex: 1, sortable: true }]

    const columnas = [
      ...columnasFijas,
      // Columnas dinámicas por día
      ...dias.map((dia) => ({
        value: `dia_${dia}`,
        key: diaToLabel(dia),
        width: 80,
        sortable: true,
        type: 'number',
      })),
    ]

    return { filas: Object.values(pedidosMap), columnas }
  }

  const { filas: datosTablaProduccion, columnas: columnasProduccionDiaria } = prepararDatosTabla()

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Título */}
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
        KPIs de Optimización
      </Typography>

      {/* Grid de KPIs */}
      {kpis && (
        <>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Métricas de Solución
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: { xs: 3, md: 4 } }}>
            <Kpi
              id="kpi-calidad-solucion"
              title="Calidad de Solución"
              subtitle="Calidad de la optimización (0-10)"
              value={kpis.calidad_solucion?.toFixed(4) || '-'}
            />
            <Kpi
              id="kpi-valor-objetivo"
              title="Valor Objetivo"
              subtitle="Valor de la función objetivo"
              value={kpis.valor_objetivo?.toFixed(2) || '-'}
            />
            <Kpi
              id="kpi-tiempo-optimizacion"
              title="Tiempo de Optimización"
              subtitle="Segundos hasta completarse"
              value={kpis.elapsed_seconds != null ? kpis.elapsed_seconds.toFixed(2) : '-'}
            />
          </Box>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Métricas Generales
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: { xs: 3, md: 4 } }}>
            <Kpi
              id="kpi-actividades-planificadas"
              title="Actividades Planificadas"
              subtitle="Total de actividades programadas"
              value={kpis.total_actividades_planificadas}
            />
            <Kpi
              id="kpi-maquinas-usadas"
              title="Máquinas Usadas"
              subtitle="Máquinas utilizadas en la planificación"
              value={kpis.maquinas_usadas}
            />
            <Kpi
              id="kpi-cantidad-arranques"
              title="Arranques"
              subtitle="Total de arranques de máquinas"
              value={kpis.cantidad_arranques}
            />
            <Kpi
              id="kpi-num-transiciones"
              title="Transiciones"
              subtitle="Cambios entre actividades"
              value={kpis.num_transiciones}
            />
            <Kpi
              id="kpi-ratio-transiciones"
              title="Ratio Transiciones/Act"
              subtitle="Transiciones por actividad"
              value={kpis.ratio_transiciones_por_actividad?.toFixed(2) || '-'}
            />
          </Box>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Cumplimiento de Plazos
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: { xs: 3, md: 4 } }}>
            <Kpi
              id="kpi-pedidos-tiempo"
              title="Pedidos a Tiempo"
              subtitle="Pedidos entregados en plazo"
              value={kpis.pedidos_a_tiempo}
            />
            <Kpi
              id="kpi-pedidos-tarde"
              title="Pedidos con Retraso"
              subtitle="Pedidos fuera de plazo"
              value={kpis.pedidos_tarde}
            />
            <Kpi
              id="kpi-max-retraso"
              title="Máximo Retraso"
              subtitle="Mayor retraso detectado (horas)"
              value={kpis.maximo_retraso}
            />
            <Kpi
              id="kpi-promedio-retraso"
              title="Promedio Retraso"
              subtitle="Retraso medio (horas)"
              value={kpis.promedio_retraso?.toFixed(2) || '0'}
            />
            <Kpi
              id="kpi-pedidos-tarde-inicio"
              title="Pedidos Tarde Inicial"
              subtitle="Pedidos con retraso desde el inicio"
              value={kpis.pedidos_tarde_desde_inicio}
            />
            <Kpi
              id="kpi-tiempo-finalizacion"
              title="Tiempo de Finalización"
              subtitle="Tiempo total de planificación (horas)"
              value={kpis.tiempo_finalizacion_planificacion}
            />
          </Box>
        </>
      )}

      {/* Producción Diaria */}
      {produccionDiaria && produccionDiaria.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Producción Diaria
          </Typography>

          {/* Gráfico de Barras */}
          <CustomBarChart
            title="Producción Diaria Planificada (Histograma)"
            labels={chartLabels}
            yAxisLabel="Toneladas"
            series={[{ data: chartData, label: 'TN Prod. Planificado', color: '#00C49F' }]}
            sx={{ mb: 3 }}
          />

          {/* Gráfico de Líneas */}
          <CustomLineChart
            title="Producción Diaria Planificada"
            labels={chartLabels}
            yAxisLabel="TN Producción"
            series={[
              {
                data: chartData,
                label: `TN Prod. Planificado — Total: ${chartData.reduce((s, v) => s + v, 0).toFixed(2)}`,
                color: '#00C49F',
                showMark: true,
              },
            ]}
            sx={{ mb: 3 }}
          />

          {/* Tabla de Producción Diaria por Pedido */}
          {datosTablaProduccion.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600 }}>
                Producción Diaria Programada
              </Typography>
              <CustomTable
                dataSource={datosTablaProduccion}
                fields={columnasProduccionDiaria}
                rowid="_rowId"
                initialPageSize={20}
                showToolbar
                density="compact"
              />
            </>
          )}
        </>
      )}
    </Box>
  )
}
